// T223 — when did something last actually HAPPEN to this application?
//
// Three places asked that question and two of them asked it wrong, in opposite
// directions:
//
//   ApplicationsPage  `rec.updatedAt ?? rec.appliedAt`
//       `updatedAt` is "last write of any kind". A rescore, a semantic pass, a
//       relevance sweep — anything that touches the row — resets the clock. On
//       2026-09-03 the "Stale (14+ days no activity)" filter reported ZERO while
//       twelve applications had genuinely been silent for two weeks, because a
//       rules-only rescore two days earlier had written to all 39 of them.
//
//   aiTriage          `rec.appliedAt`
//       Never moves. An application that advanced applied → screening yesterday
//       still reads as stale if it was submitted a month ago, so the LLM was
//       told a moving pipeline was stuck.
//
// The honest signal is the last STATUS TRANSITION — the last time the world,
// or the operator, said something changed. `statusHistory` records exactly
// that, and nothing else writes to it.
//
// Pure, no I/O, one rule for every caller.

/** Days of silence before an open application is worth chasing. */
export const STALE_DAYS = 14;

/**
 * T271(1) — the application pipeline, in one place.
 *
 * There were five parallel registries of these statuses, under a comment in
 * StatusPill claiming "all statuses surface from one shared source of truth",
 * and they had already drifted where it shows: the sidebar's "N applications in
 * active stages" summed screening+interview+offer and left out `applied` — the
 * largest group by far. Measured on 2026-09-04 the badge said 2 while the
 * tracker header, counting the same thing, said 48.
 *
 * Order is the pipeline order. `terminal` marks the stages that close an
 * application out. Colours stay per-surface on purpose: a Kanban column, a pill
 * and a chart segment legitimately want different treatments of the same stage.
 */
export const PIPELINE_STAGES = [
  { key: 'applied', label: 'Applied', terminal: false },
  { key: 'screening', label: 'Screening', terminal: false },
  { key: 'interview', label: 'Interview', terminal: false },
  { key: 'offer', label: 'Offer', terminal: false },
  { key: 'rejected', label: 'Rejected', terminal: true },
  { key: 'withdrawn', label: 'Withdrawn', terminal: true },
];

/** Every status the tracker owns — i.e. everything the dashboard queue hides. */
export const TRACKER_STATUSES = new Set(PIPELINE_STAGES.map((s) => s.key));

/**
 * T340 — the four rungs the funnel counts, and how far one application climbed.
 *
 * `rejected` and `withdrawn` are not rungs: they are how a climb ended, and an
 * application rejected after two interviews reached `interview`. So the answer
 * comes from the HIGH-WATER MARK across `statusHistory`, not from the current
 * status — "12 reached interview" and "2 are at interview" are different
 * sentences and the funnel says the first.
 *
 * Shared because the funnel bar and the list that bar opens have to agree on
 * what "reached interview" contains. This lived privately in the backend's
 * analytics service, which is fine until something clicks on it.
 */
export const FUNNEL_LADDER = Object.freeze(['applied', 'screening', 'interview', 'offer']);

/**
 * T387 — the highest rung this application ever touched, or **-1** if it never
 * touched the ladder at all.
 *
 * This is the honest reading, and `maxRung` below is the floored one. They
 * differ for exactly one case and it matters: a recommendation at `new` has
 * never been applied, and `applied` is rung **0**, so a floor at 0 makes
 * "never applied" and "applied" the same number.
 *
 * What that cost: the operator applied to an FE role on the employer's site,
 * the confirmation mail arrived, and nothing could ever propose marking it
 * applied — `proposeStage` asks `want <= currentRung`, and for an `applied`
 * signal that is `0 <= 0` for every application in existence. An `applied`
 * signal was structurally incapable of proposing anything.
 */
export function ladderRung(rec) {
  let max = FUNNEL_LADDER.indexOf(rec?.status);
  for (const h of rec?.statusHistory ?? []) {
    const i = FUNNEL_LADDER.indexOf(h?.to);
    if (i > max) max = i;
  }
  return max;
}

/**
 * Index of the highest rung this application ever touched. 0 when none.
 *
 * The floor is LOAD-BEARING for analytics and must stay: `outcomeOrdinal`
 * feeds this straight into the score-versus-outcome correlation, and 46 of his
 * rejections have an empty `statusHistory`. Returning -1 for those would move
 * their ordinal and shift a published number. Callers that need to tell
 * "never applied" from "applied" want `ladderRung`.
 */
export function maxRung(rec) {
  const r = ladderRung(rec);
  return r < 0 ? 0 : r;
}

/** Did it reach `stage` or anything past it? */
export function reachedStage(rec, stage) {
  const want = FUNNEL_LADDER.indexOf(stage);
  if (want < 0) return false;
  return maxRung(rec) >= want;
}

/** Still in play. This is what "in pipeline" means, everywhere it is counted. */
export const IN_PIPELINE_STATUSES = new Set(
  PIPELINE_STAGES.filter((s) => !s.terminal).map((s) => s.key),
);

/** Closed out. Moving INTO one of these is what the disposition modal guards. */
export const TERMINAL_STATUSES = new Set(
  PIPELINE_STAGES.filter((s) => s.terminal).map((s) => s.key),
);

/**
 * Statuses where silence is a problem rather than an outcome.
 *
 * Deliberately NOT the same set as IN_PIPELINE_STATUSES: `offer` is in play but
 * silence after an offer is the expected state, not a task. Two questions, two
 * sets — the drift above came from pretending they were one.
 */
export const ACTIVE_STATUSES = new Set(['applied', 'screening', 'interview']);

/**
 * The last time this application actually moved.
 *
 * Falls back to `appliedAt` for rows that predate `statusHistory` (T128) —
 * never to `updatedAt`, which is the whole point.
 *
 * @returns {Date|null} null when there is nothing to measure from.
 */
/**
 * T350 — the fields this rule reads, named so a caller can project them.
 *
 * `lastActivityAt` takes three inputs and a narrow `.select()` has no way to
 * know that except by reading the implementation. One did not: the daily digest
 * projected {jobId, status, appliedAt, statusHistory} and dropped
 * `followUpEmail.sentAt`, so T325 — the rule that a sent nudge counts as
 * activity — worked on the board and was inert in the digest, which then told
 * the operator an application had been silent 45 days when he had nudged it the
 * day before.
 *
 * Any query feeding this rule must project all of these.
 */
// T404 — mail on the thread is activity too, and so is applying again.
//
// T325 added the follow-up here with the argument that "sending the nudge IS
// activity", and the same argument covers these: a message arriving means
// the thread is alive, and re-applying is the operator acting on the row.
// Without them, six of his applications reported a silence that a message on
// the thread contradicted — Factbird worst at 42 days, with mail from that
// afternoon.
export const ACTIVITY_FIELDS = Object.freeze([
  'appliedAt', 'statusHistory', 'followUpEmail.sentAt', 'lastMailAt', 'reappliedAt',
]);

export function lastActivityAt(rec) {
  const hist = Array.isArray(rec?.statusHistory) ? rec.statusHistory : [];
  let latest = null;
  for (const h of hist) {
    const at = h?.at ? new Date(h.at) : null;
    if (at && !Number.isNaN(at.getTime()) && (!latest || at > latest)) latest = at;
  }
  // T325 — sending the follow-up IS activity.
  //
  // A status change is something the EMPLOYER caused; this is the one move the
  // operator can make on his own, and the product offers it precisely for
  // applications that have gone quiet. Leaving it out meant the nudge could be
  // sent and the row would go on reporting the same silence, still asking to be
  // nudged. Nothing has hit it yet — zero of his 84 applications carry a
  // `sentAt` — which is why it is cheap to fix now rather than after the first
  // one does.
  const sent = rec?.followUpEmail?.sentAt ? new Date(rec.followUpEmail.sentAt) : null;
  if (sent && !Number.isNaN(sent.getTime()) && (!latest || sent > latest)) latest = sent;
  // T404 — and mail on the thread, and applying again. Same argument as the
  // follow-up above: a row with a message on it from this afternoon is not
  // silent, whatever the last status change says.
  for (const v of [rec?.lastMailAt, rec?.reappliedAt]) {
    const at = v ? new Date(v) : null;
    if (at && !Number.isNaN(at.getTime()) && (!latest || at > latest)) latest = at;
  }
  if (latest) return latest;
  const applied = rec?.appliedAt ? new Date(rec.appliedAt) : null;
  return applied && !Number.isNaN(applied.getTime()) ? applied : null;
}

/** Whole days since the last real movement, or null when unknowable. */
export function daysSilent(rec, now = new Date()) {
  const last = lastActivityAt(rec);
  if (!last) return null;
  const ms = now.getTime() - last.getTime();
  // A timestamp in the future (clock skew, a hand-edited row) is not negative
  // silence; it is no silence.
  return ms <= 0 ? 0 : Math.floor(ms / 86_400_000);
}

/**
 * An OPEN application nobody has heard about in STALE_DAYS.
 *
 * `rejected`, `withdrawn` and `offer` are excluded: they are resolved, and
 * silence after a resolution is the expected state, not a task.
 */
export function isStale(rec, now = new Date(), threshold = STALE_DAYS) {
  if (!ACTIVE_STATUSES.has(rec?.status)) return false;
  const d = daysSilent(rec, now);
  return d !== null && d >= threshold;
}
